import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCurrentStaff, useStaffLogout, getStaffRedirectUrl } from '@/hooks/useStaffAuth';
import { useTables } from '@/hooks/useTables';
import { useAllowNegativeStock } from '@/hooks/useAllowNegativeStock';
import {
  useOrders,
  useCheckTable,
  useRequestOrderCancellation,
  useCancelParentOrderWhenAllSubOrdersCancelled,
} from '@/hooks/useOrders';
import { useKitchenSpecialties } from '@/hooks/useKitchenSpecialties';
import { TableStatusBadge } from '@/components/common/TableStatusBadge';
import { OrderModal } from '@/components/orders/OrderModal';
import { ClientNotificationsProvider } from '@/contexts/ClientNotificationsContext';
import { StaffBurgerMenu } from '@/components/layout/StaffBurgerMenu';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import {
  LogOut,
  Users,
  Utensils,
  X,
  Receipt,
  CheckCircle,
  AlertCircle,
  Languages,
} from 'lucide-react';
import type { RestaurantTable, Order } from '@/types/entities';

// Helper function to play specialty-specific notification sound
const playSpecialtyNotificationSound = (notificationSound: string) => {
  try {
    const audio = new Audio(`/notifications/${notificationSound}`);
    audio.play().catch((error) => {
      console.error('Failed to play notification sound:', error);
      // Fallback to default beep if audio file doesn't exist
      playDefaultNotificationSound();
    });
  } catch (error) {
    console.error('Failed to play notification sound:', error);
    // Fallback to default beep
    playDefaultNotificationSound();
  }
};

// Fallback function to play default notification sound using Web Audio API
const playDefaultNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const startTime = audioContext.currentTime;

    // Create a double beep pattern for portion ready (shorter than full order ready)
    const beepPattern = [
      { start: 0, duration: 0.25 }, // First beep
      { start: 0.35, duration: 0.25 }, // Second beep
    ];

    beepPattern.forEach(({ start, duration }) => {
      // Create oscillator for main tone
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();

      // Create second oscillator for richer sound (harmonic)
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();

      // Main tone: 1000 Hz
      oscillator1.frequency.value = 1000;
      oscillator1.type = 'sine';

      // Harmonic: 1500 Hz for richer sound
      oscillator2.frequency.value = 1500;
      oscillator2.type = 'sine';

      // Connect both oscillators
      oscillator1.connect(gainNode1);
      oscillator2.connect(gainNode2);
      gainNode1.connect(audioContext.destination);
      gainNode2.connect(audioContext.destination);

      const noteStart = startTime + start;
      const noteEnd = noteStart + duration;

      // Volume settings
      gainNode1.gain.setValueAtTime(0, noteStart);
      gainNode1.gain.linearRampToValueAtTime(0.6, noteStart + 0.02);
      gainNode1.gain.setValueAtTime(0.6, noteEnd - 0.02);
      gainNode1.gain.linearRampToValueAtTime(0, noteEnd);

      gainNode2.gain.setValueAtTime(0, noteStart);
      gainNode2.gain.linearRampToValueAtTime(0.35, noteStart + 0.02);
      gainNode2.gain.setValueAtTime(0.35, noteEnd - 0.02);
      gainNode2.gain.linearRampToValueAtTime(0, noteEnd);

      oscillator1.start(noteStart);
      oscillator1.stop(noteEnd);
      oscillator2.start(noteStart);
      oscillator2.stop(noteEnd);
    });
  } catch (error) {
    console.error('Failed to play default notification sound:', error);
  }
};

function ServerDashboardContent() {
  const { t, i18n } = useTranslation();
  const { data: staff } = useCurrentStaff();
  const staffLogout = useStaffLogout();
  const { data: allowNegativeStock } = useAllowNegativeStock(staff?.restaurantId || '');
  const { data: allTables, isLoading } = useTables(staff?.restaurantId || null);

  // Filter tables by staff's zone assignments
  const tables = useMemo(() => {
    if (!allTables || !staff) return allTables || [];

    // Get zone IDs from zoneIds array (preferred) or fall back to zoneId for backward compatibility
    const staffZoneIds =
      staff.zoneIds && staff.zoneIds.length > 0
        ? staff.zoneIds
        : staff.zoneId
          ? [staff.zoneId]
          : [];

    // If staff has no zones assigned, show all tables
    if (staffZoneIds.length === 0) {
      return allTables;
    }

    // If staff has zones, show ONLY tables that belong to any of those zones
    return allTables.filter((table) => table.zoneId && staffZoneIds.includes(table.zoneId));
  }, [allTables, staff]);
  const { data: orders = [] } = useOrders(staff?.restaurantId || null);
  const { data: specialtiesData } = useKitchenSpecialties(staff?.restaurantId || null);
  const specialties = specialtiesData || [];
  const checkTable = useCheckTable(staff?.restaurantId || '');
  const requestCancellation = useRequestOrderCancellation(staff?.restaurantId || '');
  const cancelParentOrder = useCancelParentOrderWhenAllSubOrdersCancelled(
    staff?.restaurantId || ''
  );

  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedTableForBill, setSelectedTableForBill] = useState<RestaurantTable | null>(null);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);

  // Ref to store the repeating sound interval for portion ready notifications
  const portionReadySoundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to stop the repeating sound
  const stopPortionReadySound = useCallback(() => {
    if (portionReadySoundIntervalRef.current) {
      clearInterval(portionReadySoundIntervalRef.current);
      portionReadySoundIntervalRef.current = null;
    }
  }, []);

  // Function to start repeating a specialty notification sound
  const startRepeatingSpecialtySound = useCallback((notificationSound: string) => {
    // Always play the immediate sound for each notification
    // This ensures each toast triggers its own sound
    playSpecialtyNotificationSound(notificationSound);

    // Stop any previous repeating sound first
    stopPortionReadySound();

    // Start a new repeating interval with the latest notification sound
    // This ensures the repeating sound is always for the most recent (last) portion
    portionReadySoundIntervalRef.current = setInterval(() => {
      playSpecialtyNotificationSound(notificationSound);
    }, 4000);
  }, [stopPortionReadySound]);

  // Listen for sub-order ready notifications (logging only - toast handled by ClientNotificationsContext)
  useEffect(() => {
    const handleSubOrderReady = (event: CustomEvent) => {
      const { tableId } = event.detail;
      const table = tables?.find((t) => t.id === tableId);
      const tableNumber = table?.tableNumber || '?';
      // Sound and toast are handled by ClientNotificationsContext with action button
      console.log('Sub-order ready event received for table:', tableNumber);
    };

    window.addEventListener('sub-order-ready', handleSubOrderReady as EventListener);
    return () => {
      window.removeEventListener('sub-order-ready', handleSubOrderReady as EventListener);
    };
  }, [tables]);

  // Listen for portion status change notifications
  useEffect(() => {
    const handlePortionStatusChanged = (event: CustomEvent) => {
      const { orderItemId, specialtyId, newStatus, oldStatus, portionId } = event.detail;

      console.log('Portion status changed event received:', {
        portionId,
        orderItemId,
        specialtyId,
        newStatus,
        oldStatus,
      });

      // Only handle when portion becomes ready
      const isBecomingReady = newStatus === 'ready' &&
        (oldStatus === undefined || oldStatus === null || oldStatus !== 'ready');

      // Find the specialty to get its notification sound
      const specialty = specialties.find((s) => s.id === specialtyId);
      const notificationSound = specialty?.notificationSound || 'notif1.mp3';

      // Find the order item in all orders to get table and menu item info
      let foundTableNumber: number | undefined;
      let foundMenuItemName: string | undefined;
      let foundSpecialtyName: string | undefined;

      // Helper function to extract item details from an item that has matching portion
      const extractItemDetails = (item: any, tableNumber?: number) => {
        foundTableNumber = tableNumber;
        foundMenuItemName = i18n.language === 'ar' ? item.menuItem?.nameAr : item.menuItem?.nameFr;

        // Find specialty name from portions or specialties list
        const portion = item.portions?.find(
          (p: any) => p.specialtyId === specialtyId || p.id === portionId
        );
        if (portion) {
          foundSpecialtyName =
            i18n.language === 'ar' ? portion.specialtyNameAr : portion.specialtyNameFr;
        }
        if (!foundSpecialtyName) {
          const specialty = specialties.find((s) => s.id === specialtyId);
          foundSpecialtyName = specialty
            ? i18n.language === 'ar'
              ? specialty.nameAr
              : specialty.nameFr
            : undefined;
        }
      };

      // Search through all orders to find the item
      outerLoop: for (const order of orders) {
        // Check items directly on parent order
        if (order.items) {
          // First try to find by orderItemId
          let item = order.items.find((i) => i.id === orderItemId);
          // Also try to find by portionId within portions
          if (!item) {
            item = order.items.find((i) => i.portions?.some((p) => p.id === portionId));
          }
          if (item) {
            extractItemDetails(item, order.table?.tableNumber);
            break outerLoop;
          }
        }

        // Check items in sub-orders
        if (order.subOrders) {
          for (const subOrder of order.subOrders) {
            // First try to find by orderItemId
            let item = subOrder.items?.find((i) => i.id === orderItemId);
            // Also try to find by portionId within portions
            if (!item) {
              item = subOrder.items?.find((i) => i.portions?.some((p) => p.id === portionId));
            }
            if (item) {
              extractItemDetails(item, subOrder.table?.tableNumber || order.table?.tableNumber);
              break outerLoop;
            }
          }
        }
      }

      console.log('Found item details:', {
        foundMenuItemName,
        foundSpecialtyName,
        foundTableNumber,
      });

      // Show green notification with repeating sound when portion becomes ready
      if (isBecomingReady) {
        console.log('Playing notification sound for ready portion');

        // Start repeating the specialty notification sound
        startRepeatingSpecialtySound(notificationSound);

        // Build notification description
        let description = '';
        if (foundMenuItemName && foundSpecialtyName) {
          description = `${foundMenuItemName} (${foundSpecialtyName})`;
          if (foundTableNumber) {
            description += ` - ${t('table.table')} ${foundTableNumber}`;
          }
        } else {
          description = t('order.portionReady');
          if (foundTableNumber) {
            description += ` - ${t('table.table')} ${foundTableNumber}`;
          }
        }

        const takeItText = t('notification.toast.orderReady.takeIt');

        // Show green toast with action button - stays until dismissed
        const { dismiss } = toast({
          title: t('notification.toast.orderReady.title'),
          description,
          duration: 1000 * 60 * 60, // 1 hour - effectively infinite until action
          className: 'bg-green-50 border-green-500 text-green-900 border-2',
          action: (
            <ToastAction
              altText={takeItText}
              onClick={() => {
                stopPortionReadySound();
                dismiss();
              }}
              className="bg-green-600 text-white hover:bg-green-700 border-green-600"
            >
              {takeItText}
            </ToastAction>
          ),
        });
      }
    };

    window.addEventListener('portion-status-changed', handlePortionStatusChanged as EventListener);
    return () => {
      window.removeEventListener(
        'portion-status-changed',
        handlePortionStatusChanged as EventListener
      );
    };
  }, [t, i18n.language, orders, specialties, startRepeatingSpecialtySound, stopPortionReadySound]);

  // Cleanup repeating sound only on component unmount
  useEffect(() => {
    return () => {
      if (portionReadySoundIntervalRef.current) {
        clearInterval(portionReadySoundIntervalRef.current);
      }
    };
  }, []);

  const handleLogout = async () => {
    await staffLogout.mutateAsync();
    // Redirect to staff login page with restaurant ID preserved
    window.location.href = getStaffRedirectUrl();
  };

  const handleTableClick = (table: RestaurantTable, e: React.MouseEvent) => {
    // Don't open modal if clicking on a button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    // Always allow opening order modal - it will handle adding to existing orders
    setSelectedTable(table);
    setIsOrderModalOpen(true);
  };

  const handleCancelSubOrder = async (subOrder: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Check if cancellation was already requested
      if (subOrder.cancellationRequestedBy && !subOrder.cancellationConfirmedBy) {
        // Confirm cancellation (if not requested by current user)
        if (subOrder.cancellationRequestedBy !== staff?.id) {
          await requestCancellation.mutateAsync({
            orderId: subOrder.id,
            staffId: staff?.id || '',
          });
          toast({
            title: t('common.success'),
            description: t('order.cancellationConfirmed'),
          });
        } else {
          toast({
            title: t('common.info'),
            description: t('order.waitingForConfirmation'),
            variant: 'default',
          });
        }
      } else {
        // Request cancellation
        await requestCancellation.mutateAsync({
          orderId: subOrder.id,
          staffId: staff?.id || '',
        });
        toast({
          title: t('common.success'),
          description: t('order.cancellationRequested'),
        });
      }
    } catch (error: any) {
      console.error('Failed to cancel order:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  const handleCancelParentOrder = async (parentOrder: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Check if cancellation was already requested
      if (parentOrder.cancellationRequestedBy && !parentOrder.cancellationConfirmedBy) {
        // Confirm cancellation (if not requested by current user)
        if (parentOrder.cancellationRequestedBy !== staff?.id) {
          await requestCancellation.mutateAsync({
            orderId: parentOrder.id,
            staffId: staff?.id || '',
          });
          toast({
            title: t('common.success'),
            description: t('order.cancellationConfirmed'),
          });
        } else {
          toast({
            title: t('common.info'),
            description: t('order.waitingForConfirmation'),
            variant: 'default',
          });
        }
      } else {
        // Request cancellation for parent order
        await cancelParentOrder.mutateAsync({
          parentOrderId: parentOrder.id,
          staffId: staff?.id || '',
        });
        toast({
          title: t('common.success'),
          description: t('order.cancellationRequested'),
        });
      }
    } catch (error: any) {
      console.error('Failed to cancel parent order:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  // Get active parent orders for a table (parent orders only, sub-orders are nested)
  // Include cancelled orders if they have non-cancelled sub-orders (table stays busy)
  const getTableParentOrders = (tableId: string) => {
    const filtered = orders.filter((order) => {
      if (order.tableId !== tableId || order.parentOrderId) {
        return false; // Only parent orders for this table
      }

      // Include if not completed/checked
      if (order.status === 'completed' || (order.status as string) === 'checked') {
        return false;
      }

      // Exclude cancelled parent orders (whether they have sub-orders or not)
      if (order.status === 'cancelled') {
        return false;
      }

      // Exclude parent orders where ALL sub-orders are cancelled
      // (they should be auto-cancelled by the backend or are in the process of being cancelled)
      if (order.subOrders && order.subOrders.length > 0) {
        const allSubOrdersCancelled = order.subOrders.every((so) => so.status === 'cancelled');
        if (allSubOrdersCancelled) {
          return false;
        }
      }

      return true;
    });
    return filtered;
  };

  // Calculate total amount for a table's orders (excluding cancelled sub-orders)
  const getTableTotal = (tableId: string) => {
    const parentOrders = getTableParentOrders(tableId);
    return parentOrders.reduce((total, parentOrder) => {
      // Calculate total from all sub-orders (excluding cancelled ones)
      const subOrdersTotal = (parentOrder.subOrders || [])
        .filter((subOrder) => subOrder.status !== 'cancelled')
        .reduce((subTotal, subOrder) => {
          const subOrderTotal =
            subOrder.items?.reduce((sum, item) => {
              const price = item.priceAtOrder || item.menuItem?.price || 0;
              return sum + price * item.quantity;
            }, 0) || 0;
          return subTotal + subOrderTotal;
        }, 0);
      return total + subOrdersTotal;
    }, 0);
  };

  // Get all parent orders for a table (for bill view, excluding completed/checked)
  // Include cancelled orders (they are persisted in DB)
  const getAllTableParentOrders = (tableId: string) => {
    return orders.filter(
      (order) =>
        order.tableId === tableId &&
        !order.parentOrderId && // Only parent orders
        order.status !== 'completed' &&
        (order.status as string) !== 'checked'
    );
  };

  const handleShowBill = (table: RestaurantTable, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent table click
    setSelectedTableForBill(table);
    setIsBillModalOpen(true);
  };

  if (!staff) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold">{t('staff.serverDashboard')}</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {t('common.welcome')}, {staff.username}
            </p>
          </div>
          {/* Mobile Burger Menu */}
          <div className="md:hidden">
            <StaffBurgerMenu onLogout={handleLogout} />
          </div>
          {/* Desktop Language & Logout */}
          <div className="hidden md:flex items-center gap-2">
            <Select value={i18n.language} onValueChange={(lang) => i18n.changeLanguage(lang)}>
              <SelectTrigger className="w-[140px]">
                <Languages className="w-4 h-4 me-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="ar">العربية</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 me-2" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xl font-semibold mb-4">{t('tables.selectTable')}</h2>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
        ) : tables && tables.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {tables.map((table) => {
              const parentOrders = getTableParentOrders(table.id);
              const hasActiveOrders = parentOrders.length > 0;

              // Check sub-orders status
              const allSubOrders = parentOrders.flatMap((po) => po.subOrders || []);
              const activeSubOrders = allSubOrders.filter((so) => so.status !== 'cancelled');
              const allSubOrdersReady =
                activeSubOrders.length > 0 && activeSubOrders.every((so) => so.status === 'ready');
              const allSubOrdersCancelled =
                allSubOrders.length > 0 && allSubOrders.every((so) => so.status === 'cancelled');

              const tableTotal = getTableTotal(table.id);

              // Calculate actual table status based on orders
              // Table stays busy if there are active orders OR if parent order is cancelled but has non-cancelled sub-orders
              const actualStatus =
                hasActiveOrders || (parentOrders.length > 0 && !allSubOrdersCancelled)
                  ? 'occupied'
                  : 'available';

              return (
                <Card
                  key={table.id}
                  className={`cursor-pointer hover:shadow-lg transition-shadow ${
                    allSubOrdersReady ? 'border-2 border-green-500 shadow-green-200' : ''
                  }`}
                  onClick={(e) => handleTableClick(table, e)}
                >
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className={`flex items-center justify-center w-12 h-12 rounded-full ${
                                allSubOrdersReady ? 'bg-green-500/20' : 'bg-primary/10'
                              }`}
                            >
                              <Utensils
                                className={`w-6 h-6 ${
                                  allSubOrdersReady ? 'text-green-600' : 'text-primary'
                                }`}
                              />
                            </div>
                            <div>
                              <div className="text-2xl font-bold">
                                {t('table.table')} {table.tableNumber || '?'}
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">
                                {table.seats} {t('table.seats')}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <TableStatusBadge status={actualStatus} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Total Price */}
                      {hasActiveOrders && tableTotal > 0 && (
                        <div className="pt-3 border-t mb-3">
                          <div
                            className="p-2 bg-primary/10 rounded-lg cursor-pointer hover:bg-primary/20 transition-colors"
                            onClick={(e) => handleShowBill(table, e)}
                          >
                            <div className="flex items-center justify-between font-semibold">
                              <span className="flex items-center gap-2 text-sm">
                                <Receipt className="w-4 h-4" />
                                {t('order.total')}
                              </span>
                              <span className="text-base font-bold">
                                {tableTotal.toFixed(2)} DH
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order Actions */}
                      {hasActiveOrders && (
                        <div className="pt-3 border-t space-y-3">
                          {/* Show parent orders with their sub-orders grouped */}
                          {parentOrders.map((parentOrder) => {
                            const parentSubOrders = parentOrder.subOrders || [];
                            if (parentSubOrders.length === 0) return null;

                            return (
                              <div
                                key={parentOrder.id}
                                className="space-y-2 p-2 bg-muted/30 rounded"
                              >
                                <div className="font-medium text-xs text-muted-foreground">
                                  {t('order.order')} #{parentOrder.id.slice(0, 8)} (
                                  {parentSubOrders.length} {t('order.items')})
                                </div>
                                {parentSubOrders.map((subOrder) => {
                                  const subOrderTotal =
                                    subOrder.items?.reduce((sum, item) => {
                                      const price = item.priceAtOrder || item.menuItem?.price || 0;
                                      return sum + price * item.quantity;
                                    }, 0) || 0;

                                  return (
                                    <div
                                      key={subOrder.id}
                                      className="p-2 border rounded text-sm"
                                      style={{
                                        borderColor:
                                          subOrder.status === 'ready'
                                            ? 'rgb(34 197 94)'
                                            : subOrder.status === 'in_progress'
                                              ? 'rgb(59 130 246)'
                                              : subOrder.status === 'cancelled'
                                                ? 'rgb(239 68 68)'
                                                : 'rgb(234 179 8)',
                                        backgroundColor:
                                          subOrder.status === 'ready'
                                            ? 'rgb(240 253 244)'
                                            : subOrder.status === 'in_progress'
                                              ? 'rgb(239 246 255)'
                                              : subOrder.status === 'cancelled'
                                                ? 'rgb(254 242 242)'
                                                : 'rgb(254 252 232)',
                                      }}
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs font-mono text-muted-foreground">
                                            #{subOrder.id.slice(0, 8)}
                                          </span>
                                          <Badge
                                            className={
                                              subOrder.status === 'ready'
                                                ? 'bg-green-500'
                                                : subOrder.status === 'in_progress'
                                                  ? 'bg-blue-500'
                                                  : subOrder.status === 'cancelled'
                                                    ? 'bg-red-500'
                                                    : 'bg-yellow-500'
                                            }
                                          >
                                            {t(`order.statuses.${subOrder.status}`)}
                                          </Badge>
                                          {subOrder.cancellationRequestedBy &&
                                            !subOrder.cancellationConfirmedBy && (
                                              <Badge className="bg-orange-500">
                                                {t('order.cancellationPending')}
                                              </Badge>
                                            )}
                                          {subOrder.status !== 'cancelled' && (
                                            <Button
                                              size="sm"
                                              variant={
                                                subOrder.cancellationRequestedBy &&
                                                !subOrder.cancellationConfirmedBy
                                                  ? 'default'
                                                  : 'destructive'
                                              }
                                              onClick={(e) => handleCancelSubOrder(subOrder, e)}
                                              disabled={
                                                requestCancellation.isPending ||
                                                (subOrder.cancellationRequestedBy &&
                                                  !subOrder.cancellationConfirmedBy) ||
                                                false
                                              }
                                              className={
                                                subOrder.cancellationRequestedBy &&
                                                !subOrder.cancellationConfirmedBy
                                                  ? 'bg-orange-500 hover:bg-orange-600 h-6 text-xs'
                                                  : 'h-6 text-xs'
                                              }
                                            >
                                              {subOrder.cancellationRequestedBy &&
                                              !subOrder.cancellationConfirmedBy ? (
                                                <>
                                                  <AlertCircle className="w-3 h-3 me-1" />
                                                  {t('order.pendingApproval')}
                                                </>
                                              ) : (
                                                <X className="w-3 h-3" />
                                              )}
                                            </Button>
                                          )}
                                        </div>
                                        <span
                                          className={`font-medium ${subOrder.status === 'cancelled' ? 'line-through text-muted-foreground' : ''}`}
                                        >
                                          {subOrderTotal.toFixed(2)} DH
                                        </span>
                                      </div>
                                      {/* Show items with portion status */}
                                      <div className="space-y-1">
                                        {(() => {
                                          // Group items by menuItemId and menuItemOptionId to combine duplicates
                                          const groupedItems = (subOrder.items || []).reduce(
                                            (acc, item) => {
                                              const key = `${item.menuItemId}-${item.menuItemOptionId || 'no-option'}`;
                                              if (!acc[key]) {
                                                acc[key] = {
                                                  ...item,
                                                  quantity: 0,
                                                  allPortions: [],
                                                };
                                              }
                                              acc[key].quantity += item.quantity;
                                              // Collect all portions from duplicate items
                                              if (item.portions && item.portions.length > 0) {
                                                acc[key].allPortions.push(...item.portions);
                                              }
                                              return acc;
                                            },
                                            {} as Record<string, any>
                                          );

                                          return Object.values(groupedItems).map((item: any) => (
                                            <div key={item.id} className="text-xs">
                                              <div className="flex justify-between items-start">
                                                <span className="text-muted-foreground">
                                                  {item.quantity}x{' '}
                                                  {i18n.language === 'ar'
                                                    ? item.menuItem?.nameAr
                                                    : item.menuItem?.nameFr}
                                                  {item.menuItemOptionId && item.menuItemOption && (
                                                    <span className="text-muted-foreground/80">
                                                      {' '}
                                                      (
                                                      {i18n.language === 'ar'
                                                        ? item.menuItemOption?.nameAr
                                                        : item.menuItemOption?.nameFr}
                                                      )
                                                    </span>
                                                  )}
                                                </span>
                                              </div>
                                              {/* Show portions if item has specialty portions */}
                                              {item.allPortions && item.allPortions.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-0.5 ml-2">
                                                  {item.allPortions.map((portion: any) => (
                                                    <Badge
                                                      key={portion.id}
                                                      variant="outline"
                                                      className="text-xs px-1 py-0"
                                                      style={{
                                                        borderColor:
                                                          portion.specialtyColor || '#888',
                                                        color: portion.specialtyColor || '#888',
                                                      }}
                                                    >
                                                      {i18n.language === 'ar'
                                                        ? portion.specialtyNameAr
                                                        : portion.specialtyNameFr}
                                                      : {t(`order.statuses.${portion.status}`)}
                                                    </Badge>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          ));
                                        })()}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}

                          {/* Cancel Parent Order button - show when all sub-orders are cancelled */}
                          {allSubOrders.length > 0 &&
                            allSubOrders.every((so) => so.status === 'cancelled') &&
                            parentOrders.length > 0 &&
                            parentOrders[0]?.status !== 'cancelled' && (
                              <Button
                                size="sm"
                                variant={
                                  parentOrders[0]?.cancellationRequestedBy &&
                                  !parentOrders[0]?.cancellationConfirmedBy
                                    ? 'default'
                                    : 'destructive'
                                }
                                className={`w-full ${parentOrders[0]?.cancellationRequestedBy && !parentOrders[0]?.cancellationConfirmedBy ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                                onClick={(e) =>
                                  handleCancelParentOrder(parentOrders[0] as Order, e)
                                }
                                disabled={
                                  cancelParentOrder.isPending ||
                                  (parentOrders[0]?.cancellationRequestedBy &&
                                    !parentOrders[0]?.cancellationConfirmedBy) ||
                                  false
                                }
                              >
                                {parentOrders[0]?.cancellationRequestedBy &&
                                !parentOrders[0]?.cancellationConfirmedBy ? (
                                  <>
                                    <AlertCircle className="w-4 h-4 me-2" />
                                    {t('order.pendingApproval')}
                                  </>
                                ) : (
                                  <>
                                    <X className="w-4 h-4 me-2" />
                                    {t('order.cancelParentOrder')}
                                  </>
                                )}
                              </Button>
                            )}

                          {/* Check Table button - only show if ALL sub-orders are ready */}
                          {allSubOrdersReady && parentOrders.length > 0 && (
                            <Button
                              size="sm"
                              className="w-full bg-green-600 hover:bg-green-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Check table (completes order and makes table available)
                                checkTable
                                  .mutateAsync({
                                    tableId: table.id,
                                  })
                                  .then(() => {
                                    toast({
                                      title: t('common.success'),
                                      description: t('order.tableChecked'),
                                    });
                                  })
                                  .catch((error) => {
                                    console.error('Failed to check table:', error);
                                    toast({
                                      title: t('common.error'),
                                      description: error.message || t('error.generic'),
                                      variant: 'destructive',
                                    });
                                  });
                              }}
                              disabled={checkTable.isPending}
                            >
                              <CheckCircle className="w-4 h-4 me-2" />
                              {t('order.checkTable')} -{' '}
                              {t('tables.tableNumber', { number: table.tableNumber })}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">{t('tables.noTables')}</div>
        )}
      </div>

      {/* Order Modal */}
      {selectedTable && (
        <OrderModal
          open={isOrderModalOpen}
          onOpenChange={setIsOrderModalOpen}
          tableId={selectedTable.id}
          tableNumber={selectedTable.tableNumber}
          restaurantId={staff.restaurantId}
          staffId={staff.id}
          allowNegativeStock={allowNegativeStock ?? false}
        />
      )}

      {/* Bill/Orders View Modal */}
      {selectedTableForBill && (
        <Dialog open={isBillModalOpen} onOpenChange={setIsBillModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {t('order.totalBill')} - {t('table.table')} #{selectedTableForBill.tableNumber}
              </DialogTitle>
              <DialogDescription>{t('order.viewAllOrdersForTable')}</DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                {getAllTableParentOrders(selectedTableForBill.id).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('order.noOrders')}
                  </div>
                ) : (
                  getAllTableParentOrders(selectedTableForBill.id).map((parentOrder) => {
                    // Calculate total from all sub-orders (excluding cancelled ones)
                    const parentTotal = (parentOrder.subOrders || [])
                      .filter((subOrder) => subOrder.status !== 'cancelled')
                      .reduce((total, subOrder) => {
                        const subOrderTotal =
                          subOrder.items?.reduce((sum, item) => {
                            const price = item.priceAtOrder || item.menuItem?.price || 0;
                            return sum + price * item.quantity;
                          }, 0) || 0;
                        return total + subOrderTotal;
                      }, 0);

                    // Calculate parent order display status based on non-cancelled sub-orders
                    const subOrders = (parentOrder.subOrders || []).filter(
                      (so) => so.status !== 'cancelled'
                    );
                    let displayStatus = parentOrder.status;

                    if (subOrders.length > 0) {
                      const allReady = subOrders.every((so) => so.status === 'ready');
                      const hasReady = subOrders.some((so) => so.status === 'ready');
                      const hasInProgress = subOrders.some((so) => so.status === 'in_progress');

                      if (allReady) {
                        displayStatus = 'ready';
                      } else if (hasReady || hasInProgress) {
                        displayStatus = 'in_progress';
                      } else {
                        displayStatus = 'pending';
                      }
                    }

                    return (
                      <Card key={parentOrder.id} className="mb-4">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-semibold">
                                {t('order.order')} #{parentOrder.id.slice(0, 8)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {new Date(parentOrder.createdAt).toLocaleString()}
                                {parentOrder.subOrders && parentOrder.subOrders.length > 0 && (
                                  <span className="ml-2">
                                    - {parentOrder.subOrders.length} {t('order.subOrders')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge
                              className={
                                displayStatus === 'ready'
                                  ? 'bg-green-500'
                                  : displayStatus === 'in_progress'
                                    ? 'bg-blue-500'
                                    : displayStatus === 'pending'
                                      ? 'bg-yellow-500'
                                      : displayStatus === 'completed' ||
                                          (displayStatus as string) === 'checked'
                                        ? 'bg-gray-500'
                                        : 'bg-red-500'
                              }
                            >
                              {t(`order.statuses.${displayStatus}`)}
                            </Badge>
                          </div>

                          {/* Show all sub-orders */}
                          {parentOrder.subOrders && parentOrder.subOrders.length > 0 && (
                            <div className="space-y-3 mb-4">
                              {parentOrder.subOrders.map((subOrder) => {
                                const subOrderTotal =
                                  subOrder.items?.reduce((sum, item) => {
                                    const price = item.priceAtOrder || item.menuItem?.price || 0;
                                    return sum + price * item.quantity;
                                  }, 0) || 0;

                                return (
                                  <div
                                    key={subOrder.id}
                                    className="border-l-2 border-primary/30 pl-3"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-xs font-medium text-muted-foreground">
                                        {t('order.subOrder')} #{subOrder.id.slice(0, 8)}
                                      </div>
                                      <Badge
                                        className={
                                          subOrder.status === 'ready'
                                            ? 'bg-green-500'
                                            : subOrder.status === 'in_progress'
                                              ? 'bg-blue-500'
                                              : 'bg-yellow-500'
                                        }
                                      >
                                        {t(`order.statuses.${subOrder.status}`)}
                                      </Badge>
                                    </div>
                                    <div className="space-y-1 mb-2">
                                      {(() => {
                                        // Group items by menuItemId and menuItemOptionId to combine duplicates
                                        const groupedItems = (subOrder.items || []).reduce(
                                          (acc, item) => {
                                            const key = `${item.menuItemId}-${item.menuItemOptionId || 'no-option'}`;
                                            if (!acc[key]) {
                                              acc[key] = {
                                                ...item,
                                                quantity: 0,
                                                allPortions: [],
                                              };
                                            }
                                            acc[key].quantity += item.quantity;
                                            // Collect all portions from duplicate items
                                            if (item.portions && item.portions.length > 0) {
                                              acc[key].allPortions.push(...item.portions);
                                            }
                                            return acc;
                                          },
                                          {} as Record<string, any>
                                        );

                                        return Object.values(groupedItems).map((item: any) => (
                                          <div
                                            key={item.id}
                                            className="p-1.5 bg-muted/50 rounded text-xs space-y-1"
                                          >
                                            <div className="flex justify-between items-start">
                                              <div className="flex-1">
                                                <p className="font-medium">
                                                  {i18n.language === 'ar'
                                                    ? item.menuItem?.nameAr
                                                    : item.menuItem?.nameFr}
                                                  {item.menuItemOptionId && item.menuItemOption && (
                                                    <span className="text-muted-foreground/80">
                                                      {' '}
                                                      (
                                                      {i18n.language === 'ar'
                                                        ? item.menuItemOption?.nameAr
                                                        : item.menuItemOption?.nameFr}
                                                      )
                                                    </span>
                                                  )}
                                                </p>
                                                {item.notes && (
                                                  <p className="text-xs text-muted-foreground mt-0.5 italic">
                                                    {t('order.notes')}: {item.notes}
                                                  </p>
                                                )}
                                              </div>
                                              <div className="text-right ml-2">
                                                <div className="flex items-center gap-1">
                                                  <Badge variant="outline" className="text-xs">
                                                    x{item.quantity}
                                                  </Badge>
                                                  <span className="font-medium text-xs">
                                                    {(
                                                      (item.priceAtOrder ||
                                                        item.menuItem?.price ||
                                                        0) * item.quantity
                                                    ).toFixed(2)}{' '}
                                                    DH
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                            {/* Show portions if item has specialty portions */}
                                            {item.allPortions && item.allPortions.length > 0 && (
                                              <div className="flex flex-wrap gap-1 mt-1 ml-2">
                                                {item.allPortions.map((portion: any) => (
                                                  <Badge
                                                    key={portion.id}
                                                    variant="outline"
                                                    className="text-xs"
                                                    style={{
                                                      borderColor: portion.specialtyColor || '#888',
                                                      color: portion.specialtyColor || '#888',
                                                    }}
                                                  >
                                                    {i18n.language === 'ar'
                                                      ? portion.specialtyNameAr
                                                      : portion.specialtyNameFr}
                                                    : {t(`order.statuses.${portion.status}`)}
                                                  </Badge>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ));
                                      })()}
                                    </div>
                                    <div className="text-xs font-medium text-right text-muted-foreground">
                                      {t('order.subOrderTotal')}: {subOrderTotal.toFixed(2)} DH
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="pt-2 border-t flex justify-between items-center font-semibold">
                            <span>{t('order.orderTotal')}</span>
                            <span>{parentTotal.toFixed(2)} DH</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>

            <div className="pt-4 border-t mt-4">
              <div className="flex justify-between items-center text-lg font-bold">
                <span className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {t('order.grandTotal')}
                </span>
                <span className="text-xl">
                  {getAllTableParentOrders(selectedTableForBill.id)
                    .reduce((total, parentOrder) => {
                      const parentTotal = (parentOrder.subOrders || []).reduce(
                        (subTotal, subOrder) => {
                          const subOrderTotal =
                            subOrder.items?.reduce((sum, item) => {
                              const price = item.priceAtOrder || item.menuItem?.price || 0;
                              return sum + price * item.quantity;
                            }, 0) || 0;
                          return subTotal + subOrderTotal;
                        },
                        0
                      );
                      return total + parentTotal;
                    }, 0)
                    .toFixed(2)}{' '}
                  DH
                </span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function ServerDashboard() {
  const { data: staff } = useCurrentStaff();

  if (!staff) {
    return null;
  }

  return (
    <ClientNotificationsProvider staff={staff} restaurantId={staff.restaurantId}>
      <ServerDashboardContent />
    </ClientNotificationsProvider>
  );
}
